import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {HTMLElement | null} */
  #footerElement = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {string} */
  #pendingReadyVariantId = '';

  /** @type {number | null} */
  #readyFallbackTimeout = null;

  /** @type {number | null} */
  #setupRetryTimeout = null;

  /** @type {number} */
  #setupRetryAttempts = 0;

  /** @type {number | null} */
  #visibilitySyncFrame = null;

  #buyButtonsEntry = null;

  #footerInRange = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    if (!this.dataset.gcReady) {
      this.dataset.gcReady = 'true';
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    this.#hideStickyBar();
    this.#ensureIntersectionObserver();
    this.#syncInitialReadyState();

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('gc:product-ready', this.#handleProductReady, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('load', this.#handleWindowLoad, { signal, once: true });
    window.addEventListener('scroll', this.#scheduleVisibilitySync, { signal, passive: true });
    window.addEventListener('resize', this.#scheduleVisibilitySync, { signal, passive: true });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;
    this.#mainBottomObserver = null;
    this.#buyButtonsEntry = null;
    this.#footerInRange = false;
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#readyFallbackTimeout) {
      clearTimeout(this.#readyFallbackTimeout);
    }
    if (this.#setupRetryTimeout) {
      clearTimeout(this.#setupRetryTimeout);
    }
    if (this.#visibilitySyncFrame) {
      cancelAnimationFrame(this.#visibilitySyncFrame);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    if (this.#buyButtonsIntersectionObserver && this.#mainBottomObserver) {
      return true;
    }

    const productForm = this.#getProductForm();
    if (!productForm) return false;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return false;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!(footer instanceof HTMLElement)) return false;

    this.#buyButtonsBlock = buyButtonsBlock;
    this.#footerElement = footer;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry || !this.isConnected) return;
      this.#buyButtonsEntry = entry;
      this.#syncStickyBarVisibility();
    });

    this.#mainBottomObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry || !this.isConnected) return;
      this.#footerInRange = entry.isIntersecting;
      this.#syncStickyBarVisibility();
    }, { rootMargin: '0px 0px 200px 0px' });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    return true;
  }

  #handleWindowLoad = () => {
    this.#ensureIntersectionObserver(true);
    this.#syncInitialReadyState();
  };

  #ensureIntersectionObserver(resetRetry = false) {
    if (resetRetry) {
      this.#setupRetryAttempts = 0;
      if (this.#setupRetryTimeout) {
        clearTimeout(this.#setupRetryTimeout);
        this.#setupRetryTimeout = null;
      }
    }

    const didSetup = this.#setupIntersectionObserver();
    if (didSetup || !this.isConnected) return didSetup;

    if (this.#setupRetryTimeout || this.#setupRetryAttempts >= 20) {
      return false;
    }

    this.#setupRetryAttempts += 1;
    this.#setupRetryTimeout = window.setTimeout(() => {
      this.#setupRetryTimeout = null;
      this.#ensureIntersectionObserver();
    }, 120);

    return false;
  }

  #syncInitialReadyState() {
    if (!this.isConnected) return;
    if (this.dataset.gcReady !== 'false' && this.dataset.gcReady !== 'pending') return;

    const sectionElement = this.closest('.shopify-section');
    const hasGcProductShell = Boolean(sectionElement?.querySelector('.gc-product-information'));
    const isBooting = document.documentElement.classList.contains('gc-product-booting');

    if (hasGcProductShell && !isBooting) {
      this.#setReadyState(true);
    }
  }

  #syncStickyBarVisibility() {
    const entry = this.#buyButtonsEntry;
    if (!entry || !this.isConnected) return;

    const buyButtonsRect = entry.boundingClientRect;
    if (entry.isIntersecting) {
      this.#hiddenByBottom = false;
      this.#hideStickyBar();
      return;
    }

    if (this.#footerInRange) {
      this.#hiddenByBottom = true;
      this.#hideStickyBar();
      return;
    }

    const isScrolledPastBuyButtons = buyButtonsRect.bottom <= 0;

    if (isScrolledPastBuyButtons && !this.#isChatActive()) {
      this.#hiddenByBottom = false;
      this.#showStickyBar();
      return;
    }

    this.#hiddenByBottom = false;
    this.#hideStickyBar();
  }

  #scheduleVisibilitySync = () => {
    if (this.#visibilitySyncFrame || !this.isConnected) return;

    this.#visibilitySyncFrame = window.requestAnimationFrame(() => {
      this.#visibilitySyncFrame = null;
      this.#ensureIntersectionObserver();
      if (this.#buyButtonsBlock && this.#buyButtonsIntersectionObserver) {
        this.#buyButtonsIntersectionObserver.unobserve(this.#buyButtonsBlock);
        this.#buyButtonsIntersectionObserver.observe(this.#buyButtonsBlock);
      }
    });
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { variantId, optionValueId } = event.detail ?? {};
    const nextVariantId = variantId || optionValueId;
    if (nextVariantId) {
      this.dataset.currentVariantId = String(nextVariantId);
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
          if (this.#pendingReadyVariantId && String(variant.id) === this.#pendingReadyVariantId) {
            this.#setReadyState(true);
          }
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Handles the product boot completion event from the PDP customization script
   * @param {CustomEvent<{variantId?: string, variantAvailable?: boolean}>} event
   */
  #handleProductReady = (event) => {
    this.#ensureIntersectionObserver(true);
    const detail = event.detail ?? {};
    const nextVariantId = detail.variantId ? String(detail.variantId) : '';

    if (typeof detail.variantAvailable === 'boolean') {
      this.dataset.variantAvailable = String(detail.variantAvailable);
    }

    if (!nextVariantId) {
      this.#setReadyState(true);
      return;
    }

    this.#pendingReadyVariantId = nextVariantId;
    this.dataset.gcReady = 'pending';

    if (this.#readyFallbackTimeout) {
      clearTimeout(this.#readyFallbackTimeout);
    }

    this.#readyFallbackTimeout = window.setTimeout(() => {
      this.#setReadyState(true);
    }, 1800);

    this.#syncReadyVariant();
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Waits for the sticky bar state to catch up to the resolved PDP variant.
   */
  #syncReadyVariant = () => {
    if (!this.isConnected) return;
    if (!this.#pendingReadyVariantId) {
      this.#setReadyState(true);
      return;
    }

    if (String(this.dataset.currentVariantId || '') === this.#pendingReadyVariantId) {
      this.#setReadyState(true);
      return;
    }

    window.requestAnimationFrame(this.#syncReadyVariant);
  };

  /**
   * Sets the sticky bar readiness flag.
   * @param {boolean} isReady
   */
  #setReadyState(isReady) {
    if (this.#readyFallbackTimeout) {
      clearTimeout(this.#readyFallbackTimeout);
      this.#readyFallbackTimeout = null;
    }

    if (isReady) {
      this.#pendingReadyVariantId = '';
      this.dataset.gcReady = 'true';
      this.#scheduleVisibilitySync();
      return;
    }

    this.dataset.gcReady = 'false';
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    if (this.#isStuck && stickyBar.dataset.stuck === 'true') return;
    this.#isStuck = true;
    stickyBar.setAttribute('aria-hidden', 'false');
    stickyBar.removeAttribute('inert');
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    if (!this.#isStuck && stickyBar.dataset.stuck === 'false' && stickyBar.hasAttribute('inert')) return;
    this.#isStuck = false;
    stickyBar.setAttribute('aria-hidden', 'true');
    stickyBar.setAttribute('inert', '');
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
